import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { parseISO, format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isToday } from 'date-fns';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import { Game, GamesByDateGroup } from '../types/Games';
import { useGameScores } from '@/features/realtime';
import { getTeamLogoUrl, teamLogoSize } from '@/shared/services/getTeamLogoUrl';
import { useVenueAwareTimeFormatter } from '../hooks/useVenueAwareTimeFormatter';
import { LocalSessionState, useAuth } from '@/features/scaffolding/components/AuthContext';
import { Tracking } from '@/shared/services/tracking';
import { cx } from '@/styles/muiTheme';
import LoadingIndicator from './LoadingIndicator';
import { Home, Video } from 'lucide-react';

const DAY_HEADERS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const CELL_SX = {
  minHeight: 148,
  height: '100%',
  boxSizing: 'border-box',
  p: '10px',
  borderRadius: 1,
} as const;

const PILL_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  borderRadius: '100px',
  whiteSpace: 'nowrap',
  flex: 'none',
  fontSize: 11,
} as const;

interface GamesContentCalendarProps {
  gamesByDate: GamesByDateGroup[];
  allGames: Game[];
  focusTeamId: number;
  currentMonth: Date;
  isFetchingPreviousGames: boolean;
  isFetchingNextGames: boolean;
}

/** Build a map of ISO date string → games[] from the flat allGames array */
function buildGamesByDateMap(allGames: Game[]): Record<string, Game[]> {
  const map: Record<string, Game[]> = {};
  for (const game of allGames) {
    if (!game.startTimestamp) continue;
    const d = typeof game.startTimestamp === 'string' ? parseISO(game.startTimestamp) : new Date(game.startTimestamp);
    const key = format(d, 'yyyy-MM-dd');
    if (!map[key]) map[key] = [];
    map[key].push(game);
  }
  return map;
}

/** Get all calendar days for a month grid (includes leading/trailing days to fill weeks) */
function getCalendarDays(month: Date): Date[] {
  const start = startOfWeek(startOfMonth(month));
  const end = endOfWeek(endOfMonth(month));
  const days: Date[] = [];
  let current = start;
  while (current <= end) {
    days.push(current);
    current = addDays(current, 1);
  }
  return days;
}

const CalendarCell: React.FC<{
  day: Date;
  games: Game[];
  focusTeamId: number;
  isCurrentMonth: boolean;
  gameScores: any;
}> = ({ day, games, focusTeamId, isCurrentMonth, gameScores }) => {
  const auth = useAuth();
  const { formatTime } = useVenueAwareTimeFormatter();
  // Show first game per day (most teams have at most 1 game/day). Days outside
  // the month are context only — their games belong to the neighbouring month.
  const game = isCurrentMonth ? games[0] : undefined;

  const isHome = game ? (game as any).homeTeam?.id === focusTeamId : false;
  const opponent = game ? (isHome ? (game as any).awayTeam : (game as any).homeTeam) : null;
  const gameStatus = game ? ((game as any).status || 'recorded') : null;
  const isLive = gameStatus === 'live';
  const isFinal = gameStatus === 'recorded';

  const isClickable = game &&
    ['live', 'recorded'].includes(gameStatus!) &&
    auth.sessionState === LocalSessionState.READY;

  const start = game?.startTimestamp
    ? (typeof game.startTimestamp === 'string' ? parseISO(game.startTimestamp) : new Date(game.startTimestamp as any))
    : null;

  const formattedTime = start
    ? formatTime(start, { formatType: 'time-only', venueTimezone: null })
    : '';

  const liveScore = game?.id !== undefined ? gameScores?.[game.id] : undefined;
  const homeScore = liveScore ? liveScore.homeScore : game?.homeTeamScore;
  const awayScore = liveScore ? liveScore.awayScore : game?.awayTeamScore;

  const focusScore = isHome ? homeScore : awayScore;
  const opponentScore = isHome ? awayScore : homeScore;
  // Home score first, whichever side the focus team is on.
  const scoreLabel = `${homeScore}–${awayScore}`;
  const resultLabel = `${focusScore > opponentScore ? 'W' : focusScore < opponentScore ? 'L' : 'T'} ${scoreLabel}`;
  const resultColor = focusScore > opponentScore
    ? cx.green
    : focusScore < opponentScore
      ? cx.red.main
      : cx.text.secondary;

  const today = isToday(day);
  const dayNumber = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 22,
        height: 22,
        px: 0.5,
        boxSizing: 'border-box',
        borderRadius: '100px',
        fontSize: 12,
        fontWeight: 500,
        bgcolor: today ? cx.blue.main : 'transparent',
        color: today
          ? cx.text.primary
          : !isCurrentMonth
            ? cx.washDark[650]
            : game
              ? cx.text.primary
              : cx.text.secondary,
      }}
    >
      {day.getDate()}
    </Box>
  );

  if (!game || !opponent) {
    return (
      <Box
        sx={{
          ...CELL_SX,
          bgcolor: isCurrentMonth ? cx.bg.surface3 : 'transparent',
          border: `1px solid ${cx.bg.surface}`,
        }}
      >
        {dayNumber}
      </Box>
    );
  }

  const cellContent = (
    <Box
      sx={{
        ...CELL_SX,
        bgcolor: isHome ? cx.bg.surface : cx.bg.surface3,
        border: '1px solid',
        borderStyle: isHome || isLive ? 'solid' : 'dashed',
        borderColor: isLive ? cx.red.main : cx.border.subtle,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        transition: 'background-color 0.15s',
        cursor: isClickable ? 'pointer' : 'default',
        '&:hover': isClickable
          ? { bgcolor: cx.bg.elevated2, '& .calendar-watch': { bgcolor: cx.blue.main, borderColor: cx.blue.main } }
          : {},
      }}
    >
      {/* Day number + Home icon, status on the right */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          {dayNumber}
          {isHome && <Home size={14} color={cx.text.secondary} style={{ flex: 'none' }} />}
        </Box>
        {isFinal && (
          <Typography sx={{ fontSize: 10, lineHeight: '14px', letterSpacing: '1px', color: cx.text.tertiary }}>
            FINAL
          </Typography>
        )}
        {isLive && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, lineHeight: '14px', letterSpacing: '1px', fontWeight: 500, color: cx.red.main }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cx.red.main }} />
            LIVE
          </Box>
        )}
      </Box>

      {/* Opponent logo */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
        <Box
          component="img"
          src={getTeamLogoUrl(opponent)}
          alt={opponent.abbreviation || opponent.name}
          sx={{ ...teamLogoSize(44, '100%'), filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }}
        />
        <Typography sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 600, letterSpacing: '0.15px', whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ fontWeight: 400, color: cx.text.tertiary }}>
            {isHome ? 'vs' : '@'}{' '}
          </Box>
          {opponent.abbreviation || opponent.name}
        </Typography>
      </Box>

      {/* Result, live score, or start time */}
      <Box sx={{ mt: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 0.75, minHeight: 22 }}>
        {isFinal && (
          <>
            <Box
              component="span"
              sx={{ ...PILL_SX, height: 20, px: 1, fontWeight: 600, letterSpacing: '0.15px', bgcolor: alpha(resultColor, 0.14), color: resultColor }}
            >
              {resultLabel}
            </Box>
            <Box
              component="span"
              className="calendar-watch"
              sx={{ ...PILL_SX, height: 22, px: 1.25, fontWeight: 500, border: `1px solid ${cx.border.default}`, color: cx.text.primary, transition: 'background-color 0.15s, border-color 0.15s' }}
            >
              <Video size={14} />
              Watch
            </Box>
          </>
        )}
        {isLive && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', whiteSpace: 'nowrap', fontFamily: cx.fontMono }}>
              <Box component="span" sx={{ fontSize: 14, lineHeight: '16px', fontWeight: 500 }}>
                {scoreLabel}
              </Box>
              {liveScore?.period && (
                <Box component="span" sx={{ fontSize: 10, lineHeight: '14px', color: cx.text.secondary }}>
                  {liveScore.period} {liveScore.time}
                </Box>
              )}
            </Box>
            <Box component="span" sx={{ ...PILL_SX, height: 22, px: 1.25, fontWeight: 500, bgcolor: cx.blue.main, color: cx.text.primary }}>
              Join
            </Box>
          </>
        )}
        {gameStatus === 'scheduled' && (
          <Typography sx={{ fontFamily: cx.fontMono, fontSize: 11, lineHeight: '14px', color: cx.text.secondary }}>
            {formattedTime}
          </Typography>
        )}
      </Box>
    </Box>
  );

  if (isClickable) {
    return (
      <Link
        to={`/games/${(game as any).uniqueId}`}
        style={{ textDecoration: 'none', color: 'inherit' }}
        onClick={() => {
          Tracking.getInstance().track(gameStatus === 'live' ? 'Join Game' : 'Watch Game', {
            category: 'Games',
            game: `${(game as any).homeTeam?.name} vs ${(game as any).awayTeam?.name}`,
          });
        }}
      >
        {cellContent}
      </Link>
    );
  }

  return cellContent;
};

const LEGEND_SWATCH_SX = { width: 12, height: 12, borderRadius: '2px', boxSizing: 'border-box' } as const;

const GamesContentCalendar: React.FC<GamesContentCalendarProps> = ({
  allGames,
  focusTeamId,
  currentMonth,
  isFetchingPreviousGames,
  isFetchingNextGames,
}) => {
  const { gameScores } = useGameScores();

  const calendarDays = useMemo(() => getCalendarDays(currentMonth), [currentMonth]);
  const gameMap = useMemo(() => buildGamesByDateMap(allGames), [allGames]);
  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7));
    }
    return result;
  }, [calendarDays]);

  const monthLabel = format(currentMonth, 'MMMM yyyy');

  // "12 games · 6 home, 6 away · 5–2" — counted off the same first-game-per-day
  // the cells show; the record covers finished (recorded) games only.
  const monthSummary = useMemo(() => {
    let games = 0;
    let home = 0;
    let wins = 0;
    let losses = 0;
    for (const day of calendarDays) {
      if (!isSameMonth(day, currentMonth)) continue;
      const game = gameMap[format(day, 'yyyy-MM-dd')]?.[0] as any;
      if (!game) continue;
      games += 1;
      const isHome = game.homeTeam?.id === focusTeamId;
      if (isHome) home += 1;
      if ((game.status || 'recorded') !== 'recorded') continue;
      const focusScore = isHome ? game.homeTeamScore : game.awayTeamScore;
      const opponentScore = isHome ? game.awayTeamScore : game.homeTeamScore;
      if (focusScore > opponentScore) wins += 1;
      else if (focusScore < opponentScore) losses += 1;
    }
    const summary = `${games} games · ${home} home, ${games - home} away`;
    return wins + losses ? `${summary} · ${wins}–${losses}` : summary;
  }, [calendarDays, currentMonth, gameMap, focusTeamId]);

  const isLoading = isFetchingPreviousGames || isFetchingNextGames;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, pb: 3 }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
          <Typography sx={{ fontSize: 36, lineHeight: '40px', fontWeight: 500 }}>
            {monthLabel}
          </Typography>
          <Typography sx={{ fontSize: 12, lineHeight: '16px', letterSpacing: '0.15px', color: cx.text.tertiary }}>
            {monthSummary}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', fontSize: 11, lineHeight: '14px', letterSpacing: '0.15px', color: cx.text.secondary }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ ...LEGEND_SWATCH_SX, bgcolor: cx.bg.elevated, border: `1px solid ${cx.border.default}` }} />
            Home
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ ...LEGEND_SWATCH_SX, bgcolor: cx.bg.surface3, border: `1px dashed ${cx.border.default}` }} />
            Away
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box component="span" sx={{ fontWeight: 600, color: cx.green }}>W</Box>
            <Box component="span" sx={{ fontWeight: 600, color: cx.red.main }}>L</Box>
            Final
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cx.red.main }} />
            Live
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Video size={14} color={cx.text.secondary} />
            Recorded
          </Box>
        </Box>
      </Box>

      {/* Loading indicator */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <LoadingIndicator message="Loading games..." />
        </Box>
      )}

      <Box>
        {/* Day-of-week headers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5, mb: 0.5 }}>
          {DAY_HEADERS.map((day) => (
            <Typography
              key={day}
              sx={{ fontSize: 11, lineHeight: '14px', letterSpacing: '1px', textTransform: 'uppercase', color: cx.text.tertiary, px: '10px' }}
            >
              {day}
            </Typography>
          ))}
        </Box>

        {/* Calendar grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5 }}>
          {weeks.flat().map((day, idx) => {
            const dateKey = format(day, 'yyyy-MM-dd');
            const dayGames = gameMap[dateKey] || [];
            return (
              <CalendarCell
                key={idx}
                day={day}
                games={dayGames}
                focusTeamId={focusTeamId}
                isCurrentMonth={isSameMonth(day, currentMonth)}
                gameScores={gameScores}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default GamesContentCalendar;
